import React, { useState } from 'react';
import { useLanguage } from '../utils/i18n';
import { useCurrency } from '../utils/useCurrency';
import { Link } from 'react-router-dom';

export default function ROICalculator({ defaultCustomers = 5, defaultSpend = 15 }) {
    const { t } = useLanguage();
    const currencySymbol = useCurrency();
    const [customers, setCustomers] = useState(defaultCustomers);
    const [spend, setSpend] = useState(defaultSpend);

    // Conservative 12% revenue boost from having a loyalty program
    const extraRevenue = Math.round(customers * spend * 30 * 0.12);

    return (
        <div className="roi-calculator-wrapper" style={{
            background: 'linear-gradient(145deg, #1A7A4A, #115e39)',
            color: 'white',
            boxShadow: '0 20px 40px rgba(26,122,74,0.2)',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <style>{`
                .roi-calculator-wrapper {
                    border-radius: 24px;
                    padding: 40px;
                }
                .roi-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 32px;
                    align-items: center;
                }
                .roi-title {
                    font-size: 28px;
                }
                .roi-result-text {
                    font-size: 48px;
                }
                @media (max-width: 768px) {
                    .roi-calculator-wrapper {
                        border-radius: 20px;
                        padding: 24px 20px;
                    }
                    .roi-grid {
                        grid-template-columns: 1fr;
                        gap: 24px;
                    }
                    .roi-title {
                        font-size: 24px;
                    }
                    .roi-result-text {
                        font-size: 36px;
                    }
                }
            `}</style>
            
            {/* Background pattern */}
            <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                opacity: 0.8
            }}></div>

            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: '32px' }}>
                <div style={{ textAlign: 'center' }}>
                    <h3 className="roi-title" style={{ fontWeight: '800', marginBottom: '8px' }}>{t('roi_title')}</h3>
                    <p style={{ opacity: 0.9 }}>{t('roi_desc')}</p>
                </div>

                <div className="roi-grid">
                    
                    {/* Controls */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontWeight: '600' }}>
                                <span>{t('roi_daily_customers')}</span>
                                <span>{customers}</span>
                            </div>
                            <input 
                                type="range" 
                                min="5" 
                                max="500" 
                                step="5"
                                value={customers} 
                                onChange={(e) => setCustomers(Number(e.target.value))}
                                style={{ width: '100%', accentColor: '#10b981' }}
                            />
                        </div>

                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontWeight: '600' }}>
                                <span>{t('roi_avg_spend')}</span>
                                <span>{currencySymbol}{spend}</span>
                            </div>
                            <input 
                                type="range" 
                                min="2" 
                                max="100"
                                step="1" 
                                value={spend} 
                                onChange={(e) => setSpend(Number(e.target.value))}
                                style={{ width: '100%', accentColor: '#10b981' }}
                            />
                        </div>
                    </div>

                    {/* Result */}
                    <div style={{ 
                        background: 'rgba(255,255,255,0.1)', 
                        backdropFilter: 'blur(10px)', 
                        borderRadius: '16px', 
                        padding: '32px 20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        border: '1px solid rgba(255,255,255,0.2)'
                    }}>
                        <span style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.9, fontWeight: '600', marginBottom: '8px' }}>
                            {t('roi_extra_revenue')}
                        </span>
                        <div className="roi-result-text" style={{ fontWeight: '800', lineHeight: 1, marginBottom: '8px', color: '#4ade80' }}>
                            +{currencySymbol}{extraRevenue.toLocaleString()}
                        </div>
                        <span style={{ opacity: 0.8 }}>{t('roi_per_month')}</span>
                        
                        <Link to="/register" style={{
                            marginTop: '24px',
                            background: '#fff',
                            color: '#1A7A4A',
                            padding: '12px 24px',
                            borderRadius: '12px',
                            fontWeight: '700',
                            textDecoration: 'none',
                            textTransform: 'uppercase',
                            fontSize: '14px',
                            letterSpacing: '0.5px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            width: '100%',
                            display: 'block'
                        }}>
                            {t('roi_cta')}
                        </Link>
                    </div>

                </div>
                
                <div style={{ fontSize: '12px', opacity: 0.6, textAlign: 'center', marginTop: '16px' }}>
                    * {t('roi_disclaimer')}
                </div>
            </div>
        </div>
    );
}
